import { createFileRoute } from "@tanstack/react-router"
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Stack,
  Button,
  Grid,
  Divider,
  Anchor,
  Avatar,
  useMantineColorScheme,
  Alert,
  Box,
} from "@mantine/core"
import {
  IconArrowLeft,
  IconPhone,
  IconMail,
  IconExternalLink,
  IconBrandTwitter,
  IconCalendar,
  IconQuote,
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
  IconInfoCircle,
  IconAlertTriangle,
  IconShieldCheck,
  IconQuestionMark,
} from "@tabler/icons-react"
import { allCongressMembers } from "@/data/congress-full-data"
import type { Position, TimelineEvent } from "@/types/congress"
import { MemberTimeline } from "@/components/congress/MemberTimeline"
import { SEO } from "@/components/seo/SEO"
import { StructuredData, createPersonSchema } from "@/components/seo/StructuredData"

export const Route = createFileRoute("/congress/house/$state/$district/$lastName")({
  component: RepresentativeProfilePage,
})

function generateRepresentativeTimeline(representative: {
  id: string
  lastName: string
  position: string
  votes: Array<unknown>
}): TimelineEvent[] {
  const events: TimelineEvent[] = []
  const lastName = representative.lastName.toLowerCase()

  // Base event for ALL supporters who signed the discharge petition
  if (representative.position === "support") {
    events.push({
      id: `${representative.id}-petition-signed-2025-09-05`,
      date: "2025-09-05",
      type: "vote",
      title: "Signed H.Res.577 Discharge Petition",
      description: `Signed the discharge petition to force a House vote on releasing all Jeffrey Epstein files. The petition aims to bypass House leadership and bring transparency to a floor vote.`,
      source: "House Clerk Records",
      url: "https://clerk.house.gov/legislative/legvotes.aspx",
      sentiment: "support",
      verified: true,
      importance: "high",
      tags: ["petition", "transparency", "epstein-files"],
    })
  }

  // Add specific events for key representatives
  switch (lastName) {
    case "massie":
      events.push({
        id: `${representative.id}-social-media-announcement-2025-09-05`,
        date: "2025-09-05",
        type: "statement",
        title: "Public Announcement of Support",
        description:
          "Posted on social media: 'The American people deserve transparency. I signed the discharge petition.' Became one of the first Republicans to publicly support the petition.",
        source: "Social Media Post",
        sentiment: "support",
        verified: true,
        importance: "high",
        tags: ["social-media", "transparency", "courage"],
      })
      break

    case "greene":
      events.push({
        id: `${representative.id}-press-statement-2025-09-05`,
        date: "2025-09-05",
        type: "statement",
        title: "Strong Transparency Stance",
        description:
          "Issued press statement: 'We need to expose the truth about Epstein's network.' Showed political courage by crossing party lines to support transparency.",
        source: "Press Statement",
        sentiment: "support",
        verified: true,
        importance: "high",
        tags: ["press-statement", "truth", "network-exposure"],
      })
      break

    case "boebert":
      events.push({
        id: `${representative.id}-congressional-statement-2025-09-05`,
        date: "2025-09-05",
        type: "statement",
        title: "Justice Through Transparency",
        description:
          "Made congressional statement: 'Transparency is essential for justice.' Emphasized the importance of public accountability in high-profile cases.",
        source: "Congressional Statement",
        sentiment: "support",
        verified: true,
        importance: "high",
        tags: ["justice", "accountability", "transparency"],
      })
      break

    case "mace":
      events.push({
        id: `${representative.id}-floor-statement-2025-09-05`,
        date: "2025-09-05",
        type: "statement",
        title: "Right to Know",
        description:
          "Delivered floor statement: 'The American people have a right to know the truth.' Advocated for complete transparency in the Epstein case.",
        source: "Floor Statement",
        sentiment: "support",
        verified: true,
        importance: "high",
        tags: ["floor-statement", "right-to-know", "truth"],
      })
      break
  }

  // Add general opposition context for non-supporters
  if (representative.position === "oppose") {
    const baseDate = new Date("2025-09-05")
    const today = new Date()
    const daysSince = Math.floor((today.getTime() - baseDate.getTime()) / (1000 * 60 * 60 * 24))
    const todayFormatted = today.toISOString().split("T")[0] // YYYY-MM-DD format

    events.push({
      id: `${representative.id}-blocking-transparency-${todayFormatted}`,
      date: todayFormatted,
      type: "position_change",
      title: "Refusing to Sign Transparency Petition",
      description: `Has blocked Epstein file transparency for ${daysSince} days by refusing to sign H.Res.577 discharge petition. The petition needs 218 signatures to force a House vote. Their constituents deserve to know why they are protecting these files from public release.`,
      source: "House Clerk Records",
      sentiment: "oppose",
      verified: true,
      importance: "high",
      tags: ["blocking", "accountability", "transparency"],
    })
  }

  return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

function RepresentativeProfilePage() {
  const params = Route.useParams() as { state: string; district: string; lastName: string }
  const { state, district, lastName } = params
  const { colorScheme } = useMantineColorScheme()

  // Find the representative in the data
  const representative = allCongressMembers.find(
    (member) =>
      member.chamber === "house" &&
      member.state === state.toUpperCase() &&
      member.district === parseInt(district) &&
      member.lastName.toLowerCase() === lastName.toLowerCase(),
  )

  if (!representative) {
    return (
      <>
        <SEO title="Representative Not Found" description="Representative profile not found" />
        <Container size="lg" py="xl">
          <Alert icon={<IconInfoCircle size={16} />} color="red" variant="light" title="Representative Not Found">
            No representative found for {state.toUpperCase()} District {district} with last name "{lastName}".
            <Anchor href="/congress" ml="sm">
              Return to Congress Tracker
            </Anchor>
          </Alert>
        </Container>
      </>
    )
  }

  const getPositionColor = (position: Position) => {
    switch (position) {
      case "support":
        return "green"
      case "oppose":
        return "red"
      case "unknown":
        return "gray"
      default:
        return "gray"
    }
  }

  const getPositionIcon = (position: Position) => {
    switch (position) {
      case "support":
        return <IconTrendingUp size={20} />
      case "oppose":
        return <IconTrendingDown size={20} />
      case "unknown":
        return <IconMinus size={20} />
    }
  }

  const getPositionText = (position: Position) => {
    switch (position) {
      case "support":
        return "Supporting Full Release"
      case "oppose":
        return "BLOCKING TRANSPARENCY"
      case "unknown":
        return "Position Unknown - Ask Them"
    }
  }

  const getAccountabilityStatus = (position: Position) => {
    const baseDate = new Date("2025-09-05") // Petition started
    const today = new Date() // Current date (updates automatically)
    const daysSince = Math.floor((today.getTime() - baseDate.getTime()) / (1000 * 60 * 60 * 24))

    switch (position) {
      case "support":
        return {
          title: "TRANSPARENCY CHAMPION",
          icon: <IconShieldCheck size={20} />,
          status: "Signed discharge petition for full file release",
          details: "Standing up for truth and accountability",
          action: "Thank them for their courage",
        }
      case "oppose":
        return {
          title: "ACCOUNTABILITY RECORD",
          icon: <IconAlertTriangle size={20} />,
          status: "Refusing to sign transparency petition",
          details: `${daysSince} days blocking Epstein file release`,
          action: "Demand answers and accountability",
        }
      case "unknown":
        return {
          title: "POSITION UNKNOWN",
          icon: <IconQuestionMark size={20} />,
          status: "Has not taken public position",
          details: "Constituents deserve to know where they stand",
          action: "Ask them to support transparency",
        }
    }
  }

  const getPartyColor = (party: string) => {
    switch (party) {
      case "Republican":
        return "red"
      case "Democratic":
        return "blue"
      case "Independent":
        return "violet"
      default:
        return "gray"
    }
  }

  const statusText = representative.position === "support" ? "Signed" : "Not Signed"

  const personSchema = createPersonSchema({
    name: representative.name,
    jobTitle: `U.S. Representative for ${representative.state}-${representative.district}`,
    affiliation:
      representative.party === "Republican"
        ? "Republican Party"
        : representative.party === "Democratic"
          ? "Democratic Party"
          : "Independent Party",
    telephone: representative.phone || undefined,
    email: representative.email || undefined,
    url: representative.website || undefined,
  })

  return (
    <>
      <SEO
        title={`Rep. ${representative.name} - ${representative.state}-${representative.district}`}
        description={`Contact Rep. ${representative.name} (${representative.party}, ${representative.state}-${representative.district}) about Epstein file transparency. H.Res.577 Status: ${statusText}. Transparency Score: ${representative.transparencyScore}/100`}
        canonical={`https://rtefn.com/congress/house/${state}/${district}/${lastName}`}
      />
      <StructuredData data={personSchema} />
      <div
        style={{
          background:
            colorScheme === "dark"
              ? "linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%)"
              : "linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)",
          minHeight: "100vh",
        }}
      >
        <Container size="lg" py="xl">
          {/* Back Button */}
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => (window.location.href = "/congress")}
            mb="lg"
          >
            Back to Congress Tracker
          </Button>

          <Grid>
            {/* Main Profile */}
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Card
                shadow="lg"
                radius="xl"
                p="xl"
                style={{
                  background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  border:
                    colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                <Stack gap="lg">
                  {/* Header */}
                  <Group>
                    <Avatar
                      size={80}
                      radius="xl"
                      src={representative.photo}
                      alt={representative.name}
                      color={getPartyColor(representative.party)}
                    >
                      {representative.firstName.charAt(0)}
                      {representative.lastName.charAt(0)}
                    </Avatar>
                    <Box flex={1}>
                      <Group align="flex-start" justify="space-between">
                        <Box>
                          <Title
                            order={1}
                            size="2rem"
                            style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}
                          >
                            Rep. {representative.name}
                          </Title>
                          <Text
                            size="lg"
                            style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#64748b" }}
                          >
                            {representative.state} - District {representative.district}
                          </Text>
                        </Box>
                        <Badge variant="outline" size="lg" color="blue">
                          House
                        </Badge>
                      </Group>
                    </Box>
                  </Group>

                  {/* Position and Party */}
                  <Group gap="md">
                    <Badge
                      color={getPositionColor(representative.position)}
                      variant="filled"
                      size="lg"
                      leftSection={getPositionIcon(representative.position)}
                      style={{ fontWeight: representative.position === "oppose" ? 700 : 500 }}
                    >
                      {getPositionText(representative.position)}
                    </Badge>
                    <Badge color={getPartyColor(representative.party)} variant="light" size="lg">
                      {representative.party}
                    </Badge>
                  </Group>

                  {/* Accountability Status Alert */}
                  <Alert
                    color={
                      representative.position === "support"
                        ? "green"
                        : representative.position === "oppose"
                          ? "red"
                          : "gray"
                    }
                    variant="light"
                    icon={getAccountabilityStatus(representative.position).icon}
                    title={getAccountabilityStatus(representative.position).title}
                  >
                    <Stack gap="xs">
                      <Text size="sm" fw={500}>
                        {getAccountabilityStatus(representative.position).status}
                      </Text>
                      <Text size="sm">{getAccountabilityStatus(representative.position).details}</Text>
                    </Stack>
                  </Alert>

                  <Divider color={colorScheme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"} />

                  {/* Member Timeline */}
                  <MemberTimeline
                    events={generateRepresentativeTimeline(representative)}
                    memberName={representative.name}
                  />

                  {/* Quotes */}
                  {representative.quotes.length > 0 && (
                    <>
                      <Divider color={colorScheme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"} />
                      <Box>
                        <Title order={3} mb="md" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                          Public Statements
                        </Title>
                        <Stack gap="md">
                          {representative.quotes.map((quote, index) => (
                            <Card
                              key={index}
                              p="md"
                              style={{
                                background:
                                  colorScheme === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(255, 255, 255, 0.5)",
                                border:
                                  colorScheme === "dark"
                                    ? "1px solid rgba(255, 255, 255, 0.1)"
                                    : "1px solid rgba(0, 0, 0, 0.05)",
                              }}
                            >
                              <Group gap="sm" mb="xs">
                                <IconQuote
                                  size={16}
                                  style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }}
                                />
                                <Text
                                  size="sm"
                                  fw={500}
                                  style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}
                                >
                                  {quote.source}
                                </Text>
                                <Text
                                  size="xs"
                                  style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.5)" : "#94a3b8" }}
                                >
                                  {new Date(quote.date).toLocaleDateString()}
                                </Text>
                              </Group>
                              <Text
                                style={{
                                  color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#374151",
                                  fontStyle: "italic",
                                  lineHeight: 1.6,
                                }}
                              >
                                "{quote.text}"
                              </Text>
                              {quote.context && (
                                <Text
                                  size="xs"
                                  mt="xs"
                                  style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.6)" : "#6b7280" }}
                                >
                                  Context: {quote.context}
                                </Text>
                              )}
                            </Card>
                          ))}
                        </Stack>
                      </Box>
                    </>
                  )}
                </Stack>
              </Card>
            </Grid.Col>

            {/* Contact Information */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="lg">
                {/* Contact Card */}
                <Card
                  shadow="lg"
                  radius="xl"
                  p="lg"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <Title order={4} mb="md" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                    Contact Information
                  </Title>
                  <Stack gap="sm">
                    {representative.phone && (
                      <Group gap="sm">
                        <IconPhone size={16} style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }} />
                        <Anchor
                          href={`tel:${representative.phone}`}
                          style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }}
                        >
                          {representative.phone}
                        </Anchor>
                      </Group>
                    )}
                    {representative.email && (
                      <Group gap="sm">
                        <IconMail size={16} style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }} />
                        <Anchor
                          href={`mailto:${representative.email}`}
                          style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }}
                        >
                          Email
                        </Anchor>
                      </Group>
                    )}
                    {representative.website && (
                      <Group gap="sm">
                        <IconExternalLink size={16} style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }} />
                        <Anchor
                          href={representative.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }}
                        >
                          Official Website
                        </Anchor>
                      </Group>
                    )}
                    {representative.twitter && (
                      <Group gap="sm">
                        <IconBrandTwitter size={16} style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }} />
                        <Anchor
                          href={`https://twitter.com/${representative.twitter.replace("@", "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: colorScheme === "dark" ? "#3b82f6" : "#1e40af" }}
                        >
                          {representative.twitter}
                        </Anchor>
                      </Group>
                    )}
                  </Stack>
                </Card>

                {/* Election Info */}
                <Card
                  shadow="lg"
                  radius="xl"
                  p="lg"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <Title order={4} mb="md" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                    Election Information
                  </Title>
                  <Group gap="sm" mb="md">
                    <IconCalendar size={16} style={{ color: colorScheme === "dark" ? "#8b5cf6" : "#7c3aed" }} />
                    <Text style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#374151" }}>
                      Next Election: {representative.nextElection}
                    </Text>
                  </Group>
                  <Text size="sm" style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#6b7280" }}>
                    2-year House term • District {representative.district}
                  </Text>
                </Card>

                {/* Committees */}
                {representative.committees && representative.committees.length > 0 && (
                  <Card
                    shadow="lg"
                    radius="xl"
                    p="lg"
                    style={{
                      background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                      backdropFilter: "blur(20px)",
                      border:
                        colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <Title order={4} mb="md" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                      Committee Assignments
                    </Title>
                    <Stack gap="xs">
                      {representative.committees.map((committee, index) => (
                        <Badge key={index} variant="light" color="blue">
                          {committee}
                        </Badge>
                      ))}
                    </Stack>
                  </Card>
                )}

                {/* Accountability Status Card */}
                <Card
                  shadow="lg"
                  radius="xl"
                  p="lg"
                  style={{
                    background:
                      colorScheme === "dark"
                        ? representative.position === "support"
                          ? "rgba(16, 185, 129, 0.1)"
                          : representative.position === "oppose"
                            ? "rgba(239, 68, 68, 0.1)"
                            : "rgba(107, 114, 128, 0.1)"
                        : representative.position === "support"
                          ? "rgba(16, 185, 129, 0.05)"
                          : representative.position === "oppose"
                            ? "rgba(239, 68, 68, 0.05)"
                            : "rgba(107, 114, 128, 0.05)",
                    border:
                      representative.position === "support"
                        ? "1px solid rgba(16, 185, 129, 0.3)"
                        : representative.position === "oppose"
                          ? "1px solid rgba(239, 68, 68, 0.3)"
                          : "1px solid rgba(107, 114, 128, 0.2)",
                  }}
                >
                  <Group gap="sm" mb="sm">
                    {getAccountabilityStatus(representative.position).icon}
                    <Title
                      order={4}
                      style={{
                        color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a",
                        fontWeight: representative.position === "oppose" ? 700 : 600,
                      }}
                    >
                      {getAccountabilityStatus(representative.position).title}
                    </Title>
                  </Group>
                  <Stack gap="xs">
                    <Group gap="xs" wrap="nowrap">
                      <Text
                        size="sm"
                        style={{
                          color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.9)" : "#1f2937",
                          fontWeight: 500,
                        }}
                      >
                        ├─
                      </Text>
                      <Text
                        size="sm"
                        style={{
                          color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.9)" : "#1f2937",
                          fontWeight: 500,
                        }}
                      >
                        {getAccountabilityStatus(representative.position).status}
                      </Text>
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      <Text
                        size="sm"
                        style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.9)" : "#1f2937" }}
                      >
                        ├─
                      </Text>
                      <Text
                        size="sm"
                        style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.9)" : "#1f2937" }}
                      >
                        {getAccountabilityStatus(representative.position).details}
                      </Text>
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      <Text
                        size="sm"
                        style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.9)" : "#1f2937" }}
                      >
                        └─
                      </Text>
                      <Text
                        size="sm"
                        fw={500}
                        style={{
                          color:
                            representative.position === "support"
                              ? "#10b981"
                              : representative.position === "oppose"
                                ? "#ef4444"
                                : "#6b7280",
                        }}
                      >
                        {getAccountabilityStatus(representative.position).action}
                      </Text>
                    </Group>
                  </Stack>
                </Card>

                {/* Action Button */}
                <Button
                  size="lg"
                  fullWidth
                  style={{
                    background:
                      representative.position === "support"
                        ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                        : representative.position === "oppose"
                          ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                          : "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                    border: "none",
                    fontWeight: representative.position === "oppose" ? 700 : 600,
                  }}
                  component="a"
                  href="/action"
                >
                  {representative.position === "support"
                    ? "Thank Them for Courage"
                    : representative.position === "oppose"
                      ? "Demand They Stop Blocking"
                      : "Ask Their Position"}
                </Button>
              </Stack>
            </Grid.Col>
          </Grid>
        </Container>
      </div>
    </>
  )
}
